import { createStyles, makeStyles } from '@material-ui/core';
import { theme } from '@saesa/ui-kit-front';
import { TipoColorLineaAvanceEnum } from 'config';

/**
 * Lado de los íconos de 24px del header mobile.
 */
const TAMANO_ICONO_HEADER = 24;

/**
 * Alto del ícono de puntos de la simbología.
 */
const ALTO_ICONO_SIMBOLOGIA = 12;

const HeaderMapaStyles = makeStyles(() =>
  createStyles({
    root: {
      position: 'absolute',
      top: '24px',
      left: '48px',
      right: '48px',
      zIndex: 4,
      display: 'flex',
      justifyContent: 'flex-start',
      pointerEvents: 'none',
      [theme.breakpoints.down('sm')]: {
        top: '16px',
        left: 0,
        right: 0,
        justifyContent: 'center',
      },
    },
    /**
     * Logo por encima del bottom sheet (solo mobile; desktop oculta con CSS)
     */
    rootSobreSheet: {
      zIndex: 9,
      pointerEvents: 'none',
      '& a': {
        pointerEvents: 'auto',
      },
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    barra: {
      pointerEvents: 'auto',
      display: 'flex',
      alignItems: 'center',
      gap: '48px',
      backgroundColor: 'var(--blanco)',
      borderRadius: '20px',
      padding: '10px 24px',
      boxShadow: '0px 2px 15px rgba(9, 39, 84, 0.14)',
      [theme.breakpoints.down('sm')]: {
        gap: 0,
        padding: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        borderRadius: 0,
      },
    },
    barraSoloLogo: {
      gap: 0,
      padding: '10px 16px',
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        borderRadius: 0,
      },
    },
    logo: {
      height: '35px',
      width: 'auto',
      display: 'block',
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
    },
    legendCnt: {
      display: 'flex',
      alignItems: 'center',
      gap: '24px',
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    legendGrupo: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    legendTitulo: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '18px',
      color: '#060709',
      whiteSpace: 'nowrap',
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    legendDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      flexShrink: 0,
    },
    dotInesperado: {
      backgroundColor: TipoColorLineaAvanceEnum.IMPREVISTO,
    },
    dotProgramado: {
      backgroundColor: '#E08A00',
    },
    dotFuturos: {
      backgroundColor: TipoColorLineaAvanceEnum.FUTURO,
    },
    legendTexto: {
      fontFamily: 'Neutrif Studio',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '18px',
      color: '#060709',
      whiteSpace: 'nowrap',
    },
    legendTextoMuted: {
      color: '#9196B1',
    },
    separador: {
      width: '1px',
      height: '12px',
      backgroundColor: TipoColorLineaAvanceEnum.PENDIENTE,
    },
    futurosCnt: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    botonReportar: {
      pointerEvents: 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#4F1FFF',
      border: '2px solid #4F1FFF',
      backgroundColor: 'var(--blanco)',
      borderRadius: '26px',
      cursor: 'pointer',
      height: '45px',
      padding: '0 32px',
      textTransform: 'none',
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '18px',
      boxShadow: 'none',
      whiteSpace: 'nowrap',
      '&:hover': {
        backgroundColor: '#F7F5FF',
        border: '2px solid #4F1FFF',
        color: '#4F1FFF',
      },
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    fabsMobile: {
      display: 'none',
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '12px',
        position: 'absolute',
        top: 'auto',
        /**
         * MensajeFlotante mobile: bottom 308px + alto del aviso PEC + ~70px del mensaje + 16px de separación.
         */
        bottom: 'calc(308px + var(--alto-disclaimer-pec, 0px) + 70px + 16px)',
        left: '16px',
        /**
         * Solo el ancho de los FABs / panel (sin right → no tapa el mapa)
         */
        right: 'auto',
        width: 'fit-content',
        maxWidth: 'calc(100vw - 32px)',
        zIndex: 4,
        pointerEvents: 'none',
        '& > *': {
          pointerEvents: 'auto',
        },
      },
    },
    fab: {
      width: '48px',
      height: '48px',
      borderRadius: '24px',
      backgroundColor: 'var(--blanco)',
      boxShadow: '0px 2px 6px rgba(9, 39, 84, 0.14)',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      position: 'relative',
      zIndex: 1,
      flexShrink: 0,
    },
    barraLeyendaMobile: {
      display: 'none',
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        /**
         * Ancho propio (el padre es fit-content y no debe forzarlo al 100% viewport)
         */
        width: 'min(343px, calc(100vw - 32px))',
        maxWidth: 'min(343px, calc(100vw - 32px))',
        height: '48px',
        padding: '0 12px',
        backgroundColor: 'var(--blanco)',
        borderRadius: '24px',
        boxShadow: '0px 2px 12px rgba(9, 39, 84, 0.14)',
        overflow: 'hidden',
        boxSizing: 'border-box',
      },
    },
    botonCerrarLeyenda: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: TAMANO_ICONO_HEADER,
      height: TAMANO_ICONO_HEADER,
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      flexShrink: 0,
    },
    barraLeyendaScroll: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      overflowX: 'auto',
      overflowY: 'hidden',
      flex: 1,
      minWidth: 0,
      height: '100%',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    modalReportarOverlay: {
      display: 'none',
      [theme.breakpoints.down('sm')]: {
        display: 'block',
        position: 'fixed',
        inset: 0,
        zIndex: 20,
      },
    },
    modalReportarBackdrop: {
      position: 'absolute',
      inset: 0,
      border: 'none',
      padding: 0,
      margin: 0,
      backgroundColor: 'rgba(7, 65, 119, 0.45)',
      cursor: 'pointer',
    },
    modalReportar: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'var(--blanco)',
      borderRadius: '20px 20px 0 0',
      boxShadow: '0px -2px 30px rgba(9, 39, 84, 0.14)',
      padding: '24px 20px 32px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '24px',
      boxSizing: 'border-box',
    },
    modalReportarCerrar: {
      alignSelf: 'flex-end',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: TAMANO_ICONO_HEADER,
      height: TAMANO_ICONO_HEADER,
      padding: 0,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      marginBottom: '-8px',
    },
    /**
     * Ícono cuadrado de los botones del header mobile.
     */
    iconoHeader: {
      display: 'block',
      width: TAMANO_ICONO_HEADER,
      height: TAMANO_ICONO_HEADER,
    },
    /**
     * Ícono de puntos de la simbología.
     */
    iconoSimbologia: {
      display: 'block',
      width: TAMANO_ICONO_HEADER,
      height: ALTO_ICONO_SIMBOLOGIA,
    },
    modalReportarTitulo: {
      margin: 0,
      fontFamily: 'Neutrif Studio',
      fontWeight: 700,
      fontSize: '21px',
      lineHeight: '28px',
      color: '#074177',
      textAlign: 'left',
      paddingRight: '8px',
    },
    modalReportarBoton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '45px',
      color: '#4F1FFF',
      border: '2px solid #4F1FFF',
      backgroundColor: 'var(--blanco)',
      borderRadius: '26px',
      cursor: 'pointer',
      textTransform: 'none',
      fontFamily: 'Neutrif Studio',
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '18px',
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: '#F7F5FF',
        border: '2px solid #4F1FFF',
        color: '#4F1FFF',
      },
    },
  })
);

export default HeaderMapaStyles;
